import { GET, POST, PUT, PATCH, DELETE } from '@/shared/api/requests';
import { StructureType, StructureTask, StructureTaskModule, ClientUserOption } from '../types';

const TRAINING_BASE = '/training';

// StructureType
export const getStructureTypes = () =>
  GET<StructureType[]>(`${TRAINING_BASE}/structure-types/`);

export const createStructureType = (data: { name: string; is_preset: boolean }) =>
  POST<typeof data>(`${TRAINING_BASE}/structure-types/`, data);

export const updateStructureType = (id: string, data: Partial<StructureType>) =>
  PUT<typeof data>(`${TRAINING_BASE}/structure-types/${id}/`, data);

export const deleteStructureType = (id: string) =>
  DELETE(`${TRAINING_BASE}/structure-types/${id}/`);

// StructureTask
export const getStructureTasks = (structureTypeId?: string) => {
  const params = structureTypeId ? `?structure_type_id=${structureTypeId}` : '';
  return GET<StructureTask[]>(`${TRAINING_BASE}/structure-tasks/${params}`);
};

export const createStructureTask = (data: { name: string; structure_type: string }) =>
  POST<typeof data>(`${TRAINING_BASE}/structure-tasks/`, data);

export const updateStructureTask = (id: string, data: Partial<StructureTask>) =>
  PUT<typeof data>(`${TRAINING_BASE}/structure-tasks/${id}/`, data);

export const patchStructureTask = (id: string, data: Partial<StructureTask>) =>
  PATCH<typeof data>(`${TRAINING_BASE}/structure-tasks/${id}/`, data);

export const deleteStructureTask = (id: string) =>
  DELETE(`${TRAINING_BASE}/structure-tasks/${id}/`);

// StructureTaskModule
export const getStructureTaskModules = (taskId?: string) => {
  const params = taskId ? `?task_id=${taskId}` : '';
  return GET<StructureTaskModule[]>(`${TRAINING_BASE}/modules/${params}`);
};

export const createStructureTaskModule = (data: {
  name: string;
  description: string;
  task: string;
  video_gcs_path: string;
  required_user_ids?: number[];
}) => POST<typeof data>(`${TRAINING_BASE}/modules/`, data);

export const updateStructureTaskModule = (id: string, data: Partial<StructureTaskModule>) =>
  PUT<typeof data>(`${TRAINING_BASE}/modules/${id}/`, data);

export const patchStructureTaskModule = (
  id: string,
  data: Partial<StructureTaskModule> & { required_user_ids?: number[]; video_gcs_path?: string },
) => PATCH<typeof data>(`${TRAINING_BASE}/modules/${id}/`, data);

export const deleteStructureTaskModule = (id: string) =>
  DELETE(`${TRAINING_BASE}/modules/${id}/`);

export const getStructureTaskModulePlaybackUrl = (id: string) =>
  GET<{ playback_url: string }>(`${TRAINING_BASE}/modules/${id}/playback-url/`);

export const markStructureTaskModuleWatched = (id: string) =>
  POST(`${TRAINING_BASE}/modules/${id}/mark-watched/`, {});

export const markStructureTaskModuleCompleteForUser = (id: string, userId: number) =>
  POST(`${TRAINING_BASE}/modules/${id}/mark-complete/`, { user_id: userId });

export const unmarkStructureTaskModuleCompleteForUser = (id: string, userId: number) =>
  POST(`${TRAINING_BASE}/modules/${id}/unmark-complete/`, { user_id: userId });

export const getClientUsersForTraining = (clientId: number) =>
  GET<{ users: ClientUserOption[] }>(`/base/clients/${clientId}/users/`);

// Signed URL for direct-to-GCS video upload
export const getVideoSignedUploadUrl = (data: {
  file_name: string;
  content_type: string;
  task_id: string;
}): Promise<{ data: { signed_url: string; gcs_path: string } }> =>
  POST<typeof data>(`${TRAINING_BASE}/video-upload-url/`, data);

/**
 * Upload a video file directly to GCS using a signed PUT URL.
 * The file never passes through our server.
 */
export const uploadVideoToGCS = (
  file: File,
  signedUrl: string,
  onProgress?: (percent: number) => void,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type);

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`GCS upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('GCS upload network error'));
    xhr.send(file);
  });
